/**
 * AdminCrossBorderReport — Phase 43.2
 *
 * Mini-report leggero per capire se l'utilizzo cross-border CH↔IT ha senso
 * durante la fase di test interna Lombardia. Nessuna analytics avanzata:
 * solo le 5 metriche concordate (count, direzione, top città, ticket medio,
 * km medi).
 */
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Globe2, ArrowRight, MapPin, Coins, Route as RouteIcon, RefreshCcw, Loader2 } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { formatCHF, formatKm } from "@/lib/format";

export default function AdminCrossBorderReport() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const { data: d } = await api.get("/admin/cross-border-report");
      setData(d);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  if (loading && !data) {
    return (
      <div className="p-8 flex items-center gap-3 text-dapnow-muted" data-testid="xb-loading">
        <Loader2 className="size-4 animate-spin" /> Calcolo report…
      </div>
    );
  }

  if (!data) return null;

  const total = data.total_cross_border_orders || 0;
  const dirs = data.directions || {};
  const empty = total === 0;

  return (
    <div className="max-w-5xl mx-auto p-4 sm:p-6 space-y-6" data-testid="admin-cross-border-report">
      <header className="flex items-start justify-between gap-3">
        <div>
          <h1 className="font-display text-2xl sm:text-3xl font-extrabold tracking-tighter inline-flex items-center gap-2">
            <Globe2 className="size-6 text-dapnow-blue" />
            Cross-border CH ↔ IT
          </h1>
          <p className="text-sm text-dapnow-muted mt-1">
            Mini-report del periodo di test Lombardia. Solo ordini pagati con paesi diversi tra ritiro e consegna.
          </p>
        </div>
        <button
          onClick={load}
          data-testid="xb-refresh"
          className="inline-flex items-center gap-1.5 rounded-xl border border-dapnow-line px-3 h-9 text-xs font-semibold hover:border-dapnow-blue/40"
        >
          <RefreshCcw className="size-3.5" /> Aggiorna
        </button>
      </header>

      {empty ? (
        <div
          className="rounded-2xl border border-dashed border-dapnow-line bg-dapnow-bg/30 p-8 text-center"
          data-testid="xb-empty"
        >
          <p className="font-semibold text-dapnow-ink">Nessun ordine cross-border pagato finora.</p>
          <p className="text-sm text-dapnow-muted mt-1">
            La Lombardia è abilitata tecnicamente per il test, ma non risultano ancora consegne reali CH↔IT.
          </p>
        </div>
      ) : (
        <>
          {/* Top metric cards */}
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
            <MetricCard
              testid="xb-total"
              icon={Globe2}
              label="Ordini cross-border"
              value={total}
              hint="Solo pagati"
            />
            <MetricCard
              testid="xb-avg-ticket"
              icon={Coins}
              label="Ticket medio"
              value={formatCHF(data.avg_ticket_chf)}
              hint="CHF per ordine"
            />
            <MetricCard
              testid="xb-avg-km"
              icon={RouteIcon}
              label="Km medi"
              value={formatKm(data.avg_distance_km)}
              hint="Distanza per ordine"
            />
            <MetricCard
              testid="xb-direction"
              icon={ArrowRight}
              label="Direzione prevalente"
              value={
                dirs["CH→IT"] >= dirs["IT→CH"]
                  ? `CH → IT (${dirs["CH→IT"] || 0})`
                  : `IT → CH (${dirs["IT→CH"] || 0})`
              }
              hint={`CH→IT ${dirs["CH→IT"] || 0} · IT→CH ${dirs["IT→CH"] || 0}`}
            />
          </div>

          {/* Direction breakdown */}
          <section className="rounded-2xl border border-dapnow-line bg-white p-5" data-testid="xb-directions">
            <h2 className="text-sm font-bold tracking-tight uppercase text-dapnow-muted">Direzione</h2>
            <div className="mt-3 space-y-2">
              <DirRow label="Svizzera → Italia" count={dirs["CH→IT"] || 0} total={total} testid="xb-dir-ch-it" />
              <DirRow label="Italia → Svizzera" count={dirs["IT→CH"] || 0} total={total} testid="xb-dir-it-ch" />
              {(dirs.other || 0) > 0 && (
                <DirRow label="Altre tratte" count={dirs.other || 0} total={total} testid="xb-dir-other" />
              )}
            </div>
          </section>

          {/* Top cities */}
          <div className="grid sm:grid-cols-2 gap-4">
            <CityList
              testid="xb-top-pickup"
              title="Top città di ritiro"
              icon={MapPin}
              iconClass="text-dapnow-blue"
              items={data.top_pickup_cities || []}
            />
            <CityList
              testid="xb-top-delivery"
              title="Top città di consegna"
              icon={MapPin}
              iconClass="text-dapnow-red"
              items={data.top_delivery_cities || []}
            />
          </div>

          <p className="text-[11px] text-dapnow-muted text-center" data-testid="xb-note">
            {data.note}
          </p>
        </>
      )}
    </div>
  );
}

function MetricCard({ icon: Icon, label, value, hint, testid }) {
  return (
    <div className="rounded-2xl border border-dapnow-line bg-white p-4" data-testid={testid}>
      <div className="inline-flex h-8 w-8 items-center justify-center rounded-lg bg-dapnow-blue-soft text-dapnow-blue">
        <Icon className="size-4" />
      </div>
      <p className="text-xs text-dapnow-muted uppercase tracking-wider font-bold mt-3">{label}</p>
      <p className="font-display text-2xl font-extrabold tracking-tighter mt-1">{value}</p>
      {hint && <p className="text-[11px] text-dapnow-muted mt-0.5">{hint}</p>}
    </div>
  );
}

function DirRow({ label, count, total, testid }) {
  const pct = total ? Math.round((count / total) * 100) : 0;
  return (
    <div data-testid={testid}>
      <div className="flex items-center justify-between text-sm">
        <span className="font-semibold text-dapnow-ink">{label}</span>
        <span className="text-dapnow-muted">{count} · {pct}%</span>
      </div>
      <div className="mt-1 h-2 rounded-full bg-dapnow-bg overflow-hidden">
        <div className="h-full bg-dapnow-blue" style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
}

function CityList({ title, icon: Icon, iconClass, items, testid }) {
  return (
    <section className="rounded-2xl border border-dapnow-line bg-white p-5" data-testid={testid}>
      <h2 className="text-sm font-bold tracking-tight uppercase text-dapnow-muted inline-flex items-center gap-1.5">
        <Icon className={`size-4 ${iconClass}`} /> {title}
      </h2>
      {items.length === 0 ? (
        <p className="text-xs text-dapnow-muted mt-3">Nessun dato.</p>
      ) : (
        <ul className="mt-3 space-y-1.5">
          {items.map((it, i) => (
            <li key={`${it.city}-${i}`} className="flex items-center justify-between text-sm" data-testid={`${testid}-row-${i}`}>
              <span className="font-medium text-dapnow-ink">{it.city}</span>
              <span className="text-dapnow-muted text-xs font-semibold">{it.count}×</span>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
